import type { BannerSwapOverlapAnalysis } from "../../../utils/@types";
import { formatSwapDateCell } from "../../BannerDashboard/bannerDashboardUtils";
import type { SwapOverlapResolutionChoice } from "./SwapOverlapResolutionDialog";

export type OverlapResolutionCallout = {
  id: string;
  channelId?: string;
  tone: "neutral" | "remove" | "later" | "early" | "kept";
  text: string;
};

function shortChannelId(channelId: string): string {
  if (channelId.length <= 18) {
    return channelId;
  }
  return `${channelId.slice(0, 10)}…${channelId.slice(-4)}`;
}

function formatWhen(raw: string | undefined): string {
  if (!raw) {
    return "—";
  }
  return formatSwapDateCell(raw);
}

export function buildOverlapResolutionCallouts(
  option: SwapOverlapResolutionChoice,
  overlap: BannerSwapOverlapAnalysis
): OverlapResolutionCallout[] {
  const callouts: OverlapResolutionCallout[] = [];
  const preview = overlap.preview;
  if (!preview) {
    return callouts;
  }

  if (option === "remove_conflicts") {
    for (const channelId of overlap.conflictChannelIds) {
      callouts.push({
        id: `remove-${channelId}`,
        channelId,
        tone: "remove",
        text: `${shortChannelId(channelId)} will be removed from this swap.`,
      });
    }
    for (const channelId of overlap.cleanChannelIds) {
      const ch = preview.remove_conflicts.channels.find(
        (c) => c.channelId === channelId
      );
      callouts.push({
        id: `kept-${channelId}`,
        channelId,
        tone: "kept",
        text: `${shortChannelId(channelId)} will swap on ${formatWhen(ch?.startDate)} – ${formatWhen(ch?.endDate)}.`,
      });
    }
    return callouts;
  }

  if (option === "tail_existing_priority") {
    for (const channelId of overlap.conflictChannelIds) {
      const ch = preview.tail_existing_priority.channels.find(
        (c) => c.channelId === channelId
      );
      if (!ch) {
        continue;
      }
      callouts.push({
        id: `tail-start-${channelId}`,
        channelId,
        tone: "later",
        text: `${shortChannelId(channelId)} will start later on ${formatWhen(ch.startDate)} and still finish on ${formatWhen(ch.endDate)}.`,
      });
    }
    for (const channelId of overlap.cleanChannelIds) {
      const ch = preview.tail_existing_priority.channels.find(
        (c) => c.channelId === channelId
      );
      if (!ch) {
        continue;
      }
      callouts.push({
        id: `tail-clean-${channelId}`,
        channelId,
        tone: "kept",
        text: `${shortChannelId(channelId)} will swap on schedule (${formatWhen(ch.startDate)} – ${formatWhen(ch.endDate)}).`,
      });
    }
    let latestBlockerEnd: string | undefined;
    let latestBlockerUid = "";
    for (const conflict of overlap.conflicts) {
      for (const swap of conflict.conflictingSwaps) {
        if (
          !latestBlockerEnd ||
          new Date(swap.endDate).getTime() > new Date(latestBlockerEnd).getTime()
        ) {
          latestBlockerEnd = swap.endDate;
          latestBlockerUid = swap.swapUID;
        }
      }
    }
    if (latestBlockerEnd && latestBlockerUid) {
      callouts.push({
        id: `tail-blocker-${latestBlockerUid}`,
        tone: "neutral",
        text: `Swap #${latestBlockerUid} keeps priority and finishes on ${formatWhen(latestBlockerEnd)}.`,
      });
    }
    return callouts;
  }

  if (option === "tail_incoming_priority") {
    for (const channelId of overlap.conflictChannelIds) {
      const ch = preview.tail_incoming_priority.channels.find(
        (c) => c.channelId === channelId
      );
      if (!ch) {
        continue;
      }
      callouts.push({
        id: `priority-incoming-${channelId}`,
        channelId,
        tone: "kept",
        text: `${shortChannelId(channelId)} will swap on ${formatWhen(ch.startDate)} – ${formatWhen(ch.endDate)}.`,
      });
    }
    for (const channelId of overlap.cleanChannelIds) {
      const ch = preview.tail_incoming_priority.channels.find(
        (c) => c.channelId === channelId
      );
      if (!ch) {
        continue;
      }
      callouts.push({
        id: `priority-clean-${channelId}`,
        channelId,
        tone: "kept",
        text: `${shortChannelId(channelId)} will swap on schedule (${formatWhen(ch.startDate)} – ${formatWhen(ch.endDate)}).`,
      });
    }
    for (const adj of preview.tail_incoming_priority.adjustedBlockers) {
      callouts.push({
        id: `priority-early-${adj.swapUID}-${adj.channelId}`,
        channelId: adj.channelId,
        tone: "early",
        text: `Swap #${adj.swapUID} on ${shortChannelId(adj.channelId)} will finish early on ${formatWhen(adj.newEndDate)} (was ${formatWhen(adj.previousEndDate)}).`,
      });
    }
    return callouts;
  }

  if (option === "truncate_incoming_priority") {
    for (const channelId of overlap.conflictChannelIds) {
      const ch = preview.truncate_incoming_priority.channels.find(
        (c) => c.channelId === channelId
      );
      if (!ch) {
        continue;
      }
      callouts.push({
        id: `truncate-${channelId}`,
        channelId,
        tone: "early",
        text: `${shortChannelId(channelId)} will end early on ${formatWhen(ch.endDate)} (was ${formatWhen(overlap.preview?.remove_conflicts.channels.find((c) => c.channelId === channelId)?.endDate)}).`,
      });
    }
    for (const channelId of overlap.cleanChannelIds) {
      const ch = preview.truncate_incoming_priority.channels.find(
        (c) => c.channelId === channelId
      );
      if (!ch) {
        continue;
      }
      callouts.push({
        id: `truncate-clean-${channelId}`,
        channelId,
        tone: "kept",
        text: `${shortChannelId(channelId)} will swap on schedule (${formatWhen(ch.startDate)} – ${formatWhen(ch.endDate)}).`,
      });
    }
    return callouts;
  }

  if (option === "delay_existing_start_priority") {
    for (const channelId of overlap.conflictChannelIds) {
      const ch = preview.delay_existing_start_priority.channels.find(
        (c) => c.channelId === channelId
      );
      if (!ch) {
        continue;
      }
      callouts.push({
        id: `delay-incoming-${channelId}`,
        channelId,
        tone: "kept",
        text: `${shortChannelId(channelId)} will swap on ${formatWhen(ch.startDate)} – ${formatWhen(ch.endDate)}.`,
      });
    }
    for (const channelId of overlap.cleanChannelIds) {
      const ch = preview.delay_existing_start_priority.channels.find(
        (c) => c.channelId === channelId
      );
      if (!ch) {
        continue;
      }
      callouts.push({
        id: `delay-clean-${channelId}`,
        channelId,
        tone: "kept",
        text: `${shortChannelId(channelId)} will swap on schedule (${formatWhen(ch.startDate)} – ${formatWhen(ch.endDate)}).`,
      });
    }
    for (const adj of preview.delay_existing_start_priority.adjustedPeers) {
      callouts.push({
        id: `delay-late-${adj.swapUID}-${adj.channelId}`,
        channelId: adj.channelId,
        tone: "later",
        text: `Swap #${adj.swapUID} on ${shortChannelId(adj.channelId)} will start later on ${formatWhen(adj.newStartDate)} (was ${formatWhen(adj.previousStartDate)}).`,
      });
    }
    return callouts;
  }

  if (option === "drop_peer_conflicts") {
    for (const channelId of overlap.conflictChannelIds) {
      const ch = preview.drop_peer_conflicts.channels.find(
        (c) => c.channelId === channelId
      );
      if (!ch) {
        continue;
      }
      callouts.push({
        id: `drop-incoming-${channelId}`,
        channelId,
        tone: "kept",
        text: `${shortChannelId(channelId)} will swap on ${formatWhen(ch.startDate)} – ${formatWhen(ch.endDate)}.`,
      });
    }
    for (const channelId of overlap.cleanChannelIds) {
      const ch = preview.drop_peer_conflicts.channels.find(
        (c) => c.channelId === channelId
      );
      if (!ch) {
        continue;
      }
      callouts.push({
        id: `drop-clean-${channelId}`,
        channelId,
        tone: "kept",
        text: `${shortChannelId(channelId)} will swap on schedule (${formatWhen(ch.startDate)} – ${formatWhen(ch.endDate)}).`,
      });
    }
    const seenCancel = new Set<string>();
    for (const adj of preview.drop_peer_conflicts.adjustedPeers) {
      if (adj.action === "cancel_swap") {
        if (seenCancel.has(adj.swapUID)) {
          continue;
        }
        seenCancel.add(adj.swapUID);
        callouts.push({
          id: `drop-cancel-${adj.swapUID}`,
          tone: "remove",
          text: `Swap #${adj.swapUID} will be cancelled (conflicting channel${adj.remainingChannelCount === 0 ? "" : "s"} removed).`,
        });
      } else {
        callouts.push({
          id: `drop-remove-${adj.swapUID}-${adj.channelId}`,
          channelId: adj.channelId,
          tone: "remove",
          text: `Swap #${adj.swapUID} will drop ${shortChannelId(adj.channelId)} (${adj.remainingChannelCount} channel${adj.remainingChannelCount === 1 ? "" : "s"} remain).`,
        });
      }
    }
  }

  return callouts;
}
